import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import IvisoraLogo from '../../components/shared/IvisoraLogo';
import { loginPath } from '../../lib/authRedirect';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTH_TOKEN_KEY = 'ivisora_auth_token';
const USER_KEY = 'ivisora_user';

const CreatorOnboardingProfile = () => {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [availableForHire, setAvailableForHire] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) { navigate(loginPath(window.location.pathname), { replace: true }); return; }
    try {
      const u = JSON.parse(localStorage.getItem(USER_KEY) || '{}');
      setDisplayName(u.name || '');
    } catch {}
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!displayName.trim()) { setError('Display name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const r = await fetch(`${API_URL}/api/creator/onboarding/complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_name: displayName.trim(), bio, available_for_hire: availableForHire }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Something went wrong.');
      }
      const data = await r.json();
      // Update cached user role
      try {
        const u = JSON.parse(localStorage.getItem(USER_KEY) || '{}');
        localStorage.setItem(USER_KEY, JSON.stringify({ ...u, role: data.role }));
      } catch {}
      navigate('/creator/onboarding/first-template');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-12">
      {/* Logo */}
      <div className="mb-8">
        <IvisoraLogo className="h-8 mx-auto" />
      </div>

      {/* Progress */}
      <div className="w-full max-w-md mb-8">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-violet-600 text-white text-xs font-bold flex items-center justify-center">1</div>
            <span className="text-sm font-semibold text-violet-600">Creator Profile</span>
          </div>
          <div className="flex-1 h-px bg-gray-200" />
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full border-2 border-gray-200 text-gray-400 text-xs font-bold flex items-center justify-center">2</div>
            <span className="text-sm text-gray-400">First Template</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 w-full max-w-md p-8">
        <div className="mb-6 text-center">
          <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-6 h-6 text-violet-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Set up your creator profile</h1>
          <p className="text-sm text-gray-500 mt-1">This is how buyers will see you on the marketplace.</p>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Display name <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="e.g. Alice Designs"
              maxLength={60}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Bio</label>
            <textarea
              value={bio}
              onChange={e => setBio(e.target.value)}
              placeholder="Tell buyers a little about yourself and your design style."
              rows={3}
              maxLength={300}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{bio.length}/300</p>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={availableForHire}
              onChange={e => setAvailableForHire(e.target.checked)}
              className="w-4 h-4 rounded text-violet-600 focus:ring-violet-500"
            />
            <span className="text-sm text-gray-700">I'm available for hire / custom work</span>
          </label>

          <button
            type="submit"
            disabled={saving || !displayName.trim()}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {saving ? 'Saving…' : <>Continue <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreatorOnboardingProfile;
